import * as React from 'react'
import TopNavigation from '../components/Navigation/TopNavigation'
import TopNavigationButtonWithOptions from '../components/Navigation/TopNavigationButtonWithOptions'
import { AppState } from '../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ERoute from '../ERoute'
import { RouteComponentProps } from 'react-router'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import { showBulkContactGroupModal, showConfirmModal, showContactModal, showExportContactsModal } from '../store/modals/actions'
import PageContent from '../components/Page/PageContent'
import PageHeader from '../components/Page/PageHeader'
import { Helmet } from 'react-helmet'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Contact, ContactFilter, ContactFilters, ContactType, CurrentUser, ResourceListFilterType } from '../types'
import LocalStorage, { LocalStorageKey } from '../LocalStorage'
import CardEmptyInfo from '../components/Card/CardEmptyInfo'
import ResourceTable, { ResourceTableAction } from '../components/Resource/ResourceTable'
import PageLoader from '../components/Page/PageLoader'
import { ContactsController } from '../controllers'
import RouteHelper from '../helpers/RouteHelper'
import Notification from '../utilities/Notification'
import { LOCALES } from '../Constants'
import ContactHelper from '../helpers/ContactHelper'
import SearchHelper from '../helpers/SearchHelper'
import NavigationDetection from '../components/Effects/NavigationDetection'
import getContactTableRenderer from '../components/Resource/ContactRenderer'

interface IStateProps extends RouteComponentProps<{ groupId?: string }> {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showContactModal: typeof showContactModal
  showConfirmModal: typeof showConfirmModal
  showBulkContactGroupModal: typeof showBulkContactGroupModal
  showExportContactsModal: typeof showExportContactsModal
}

interface IState {
  contacts: Contact[],
  page: number,
  perPage: number,
  totalPages: number
  didInitialLoad: boolean
  isFetching: boolean
  sortValue: string
  activeFilters: any,
  selectedContactIds: string[]
  search: string
}

type IProps = IStateProps & IDispatchToProps & WithTranslation

class Contacts extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, ContactFilters)

    this.state = {
      contacts: [],
      page: page,
      perPage: perPage,
      totalPages: 0,
      didInitialLoad: false,
      isFetching: false,
      sortValue: LocalStorage.get(LocalStorageKey.CONTACT_SORT_VALUE, '-'),
      selectedContactIds: [],
      search: search,
      activeFilters: filters,
    }

    this.onRouteChange = this.onRouteChange.bind(this)
    this.onTopNavigationActionClick = this.onTopNavigationActionClick.bind(this)
    this.onPageHeaderActionClick = this.onPageHeaderActionClick.bind(this)
    this.onExportContactsClick = this.onExportContactsClick.bind(this)
    this.onNewContactClick = this.onNewContactClick.bind(this);
    this.onTableContactClick = this.onTableContactClick.bind(this)
    this.onTableActionClick = this.onTableActionClick.bind(this)
    this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
    this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
    this.onTableContactArchiveClick = this.onTableContactArchiveClick.bind(this)
    this.onTableContactUpdateClick = this.onTableContactUpdateClick.bind(this)
    this.onTableContactDeleteClick = this.onTableContactDeleteClick.bind(this);
    this.onContactFormSubmit = this.onContactFormSubmit.bind(this)
    this.onContactUpdateFormSubmit = this.onContactUpdateFormSubmit.bind(this)
    this.onContactCreate = this.onContactCreate.bind(this)
    this.onContactFiltersChange = this.onContactFiltersChange.bind(this)
    this.onContactSortValueChange = this.onContactSortValueChange.bind(this)
    this.onContactSearchChange = this.onContactSearchChange.bind(this)
    this.onContactSearchSubmit = this.onContactSearchSubmit.bind(this)
    this.onContactClearFilters = this.onContactClearFilters.bind(this)
    this.onAddGroupsClick = this.onAddGroupsClick.bind(this)
    this.onRemoveGroupsClick = this.onRemoveGroupsClick.bind(this)
    this.onBulkDeleteClick = this.onBulkDeleteClick.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPerPageChange = this.onPerPageChange.bind(this)
    this.onColumnsChange = this.onColumnsChange.bind(this)
  }

  onRouteChange() {
    const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, ContactFilters)

    this.setState({
      search: search,
      page: page,
      perPage: perPage,
      activeFilters: filters
    }, () => {
      this.fetchContacts(page)
    })
  }

  onTopNavigationActionClick(key: string) {
    const { showContactModal } = this.props
    switch (key) {
      case 'person':
      case 'company':
        showContactModal({
          contact: { type: key as ContactType },
          onSubmit: this.onContactFormSubmit,
        })
        break
      default:
        throw new Error(`[Contacts] action not implemented for key: ${key} `)
    }
  }

  onPageHeaderActionClick(key: string) {
    switch (key) {
      case 'export_contacts': this.onExportContactsClick()
        break
    }
  }

  async onExportContactsClick() {
    this.props.showExportContactsModal({})
  }

  async fetchContacts(page: number) {
    const { search, sortValue, activeFilters, perPage } = this.state
    this.setState({
      isFetching: true
    }, async () => {
      try {
        const response = await ContactsController.getContacts({
          page: page,
          per_page: perPage,
          search: search,
          order: `${sortValue}`,
          ...activeFilters
        })
        const { contacts, current_page, total_pages, total_entries } = response;

        this.setState({
          contacts: [...contacts],
          page: current_page,
          totalPages: total_pages,
          didInitialLoad: true,
          isFetching: false
        });
      } catch (ex) {
        console.error(ex)
      }
    });
  }

  onNewContactClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault()

    const { showContactModal } = this.props

    showContactModal({
      contact: { type: ContactType.PERSON },
      onSubmit: this.onContactFormSubmit,
    })
  }

  onTableContactClick(contact: Contact) {
    this.props.history.push(RouteHelper.process(ERoute.PATH_CONTACT, { id: contact.id }))
  }

  onTableActionClick(key: string, contact: Contact) {
    switch (key) {
      case 'archive': this.onTableContactArchiveClick(contact)
        break
      case 'edit': this.onTableContactUpdateClick(contact)
        break
      case 'delete': this.onTableContactDeleteClick(contact)
        break
      default:
        throw Error('[Contacts] Unimplemented onTableActionClick')
    }
  }

  onTableSelectionChange(selectedContactIds: string[]) {
    this.setState({ selectedContactIds: selectedContactIds })
  }

  onTableRowSelectionChange(selected: boolean, transactionId: string) {
    const { selectedContactIds } = this.state

    if (selected) {
      this.setState({ selectedContactIds: [...selectedContactIds, transactionId] })
    } else {
      this.setState({ selectedContactIds: selectedContactIds.filter(selectedTransactionId => selectedTransactionId !== transactionId) })
    }
  }

  async onTableContactArchiveClick(contact: Contact) {
    try {
      const updatedContact = await ContactsController.update({ ...contact, archived: !contact.archived })

      this.onContactUpdateFormSubmit(updatedContact)
    } catch (ex) {
      console.error(ex)
    }
  }

  onTableContactUpdateClick(contact: Contact) {
    const { showContactModal } = this.props

    showContactModal({
      contact: { id: contact.id, type: contact.type },
      onSubmit: this.onContactUpdateFormSubmit,
    })
  }

  onTableContactDeleteClick(contact: Contact) {
    const { showConfirmModal, t } = this.props

    showConfirmModal({
      title: t('Contact::Delete contact'),
      description: t('Contact::You are about to delete {{name}}. By deleting this contact you are also deleting all its associated data. Are you sure?', { name: contact.name }),
      action: { label: t('Contact::Delete'), isDestructive: true },
      onConfirm: () => {
        ContactsController
          .delete(contact.id)
          .then((response) => {
            if (response.errors) {

            } else {
              const { contacts } = this.state;

              const contactIndex = contacts.findIndex(c => c.id === contact.id);

              contacts.splice(contactIndex, 1);

              this.setState({
                contacts: contacts
              });

              Notification.notifySuccess(t('Contact::Contact successfully deleted'))
            }
          })
          .catch(console.error)
      }
    })
  }

  onContactFormSubmit(contact: Contact) {
    const { contacts } = this.state;

    const newContacts = [contact, ...contacts];

    this.setState({ contacts: newContacts });
  }

  onContactUpdateFormSubmit(contact: Contact) {
    const { contacts } = this.state

    const contactIndex = contacts.findIndex(c => c.id === contact.id);

    if (contactIndex !== -1) {
      contacts[contactIndex] = contact
    }

    this.setState({
      contacts: [
        ...contacts,
      ]
    })
  }

  onContactCreate(data) {
    const { contacts } = this.state
    const { detail: { contact } } = data

    this.setState({
      contacts: [
        contact,
        ...contacts,
      ]
    })
  }

  onContactFiltersChange(activeFilters: any) {
    this.setState({ page: 1, activeFilters: activeFilters }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onContactSortValueChange(value: string) {
    LocalStorage.set(LocalStorageKey.CONTACT_SORT_VALUE, value)
    this.setState({ page: 1, sortValue: value }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onContactSearchChange(search) {
    this.setState({ search: search })
  }

  onContactSearchSubmit(search) {
    this.setState({ page: 1, search: search }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onContactClearFilters() {
    this.setState({
      page: 1,
      search: '',
      activeFilters: {}
    }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onAddGroupsClick() {
    const { selectedContactIds } = this.state

    requestAnimationFrame(() => {
      this.props.showBulkContactGroupModal({
        action: 'add',
        contactIds: selectedContactIds,
        onSubmit: (_contactGroups) => {
          this.setState({ selectedContactIds: [] })
          this.fetchContacts(this.state.page)
        }
      })
    })
  }

  onRemoveGroupsClick() {
    const { selectedContactIds } = this.state

    requestAnimationFrame(() => {
      this.props.showBulkContactGroupModal({
        action: 'remove',
        contactIds: selectedContactIds,
        onSubmit: (_contactGroups) => {
          this.setState({ selectedContactIds: [] })
          this.fetchContacts(this.state.page)
        }
      })
    })
  }

  onBulkDeleteClick() {
    const { showConfirmModal, t } = this.props
    const { contacts, selectedContactIds } = this.state

    showConfirmModal({
      title: t('Contact::Delete contacts'),
      description: t('Contact::You are about to delete {{count}} contacts. By deleting these contacts you are also deleting all their associated data. Are you sure?', { count: selectedContactIds.length }),
      action: { label: t('Contact::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          await ContactsController.bulkDelete(selectedContactIds)

          this.setState({
            selectedContactIds: [],
            contacts: contacts.filter(contact => !selectedContactIds.includes(contact.id))
          })
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  onPageChange(page: number) {
    this.setState({ page: page }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onPerPageChange(perPage: number) {
    this.setState({ page: 1, perPage: perPage }, () => {
      const { page, activeFilters, search, sortValue, perPage } = this.state
      LocalStorage.set(LocalStorageKey.PER_PAGE, perPage)
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue, perPage: perPage })
    })
  }

  onColumnsChange(columns: string[]) {
    LocalStorage.set(LocalStorageKey.CONTACT_COLUMNS, columns)
    this.forceUpdate()
  }

  render() {
    const { currentUser, t } = this.props
    const { contacts, didInitialLoad, isFetching, activeFilters, sortValue, search, page, perPage, totalPages, selectedContactIds } = this.state
    const { workspace: { setting } } = currentUser

    const activeFiltersCount = search?.length > 0 || Object.keys(activeFilters).length > 0
    const promotedBulkActions: ResourceTableAction[] = [
      { icon: 'tag', content: t('Contacts::Add to group'), onAction: this.onAddGroupsClick },
      { icon: 'circle-xmark', content: t('Contacts::Remove from group'), onAction: this.onRemoveGroupsClick },
      { icon: 'trash-alt-solid', content: t('Contacts::Delete'), onAction: this.onBulkDeleteClick, destructive: true }
    ]

    const bulkActions: ResourceTableAction[] = []
    const filters: ContactFilter[] = [
      { name: 'name', label: t('Contact::Name'), type: ResourceListFilterType.STRING },
      { name: 'alias', label: t('Contact::Alias'), type: ResourceListFilterType.STRING },
      { name: 'type', label: t('Contact::Record type'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: ContactHelper.getContactLabelType(ContactType.COMPANY), value: ContactType.COMPANY }, { label: ContactHelper.getContactLabelType(ContactType.PERSON), value: ContactType.PERSON }] },
      { name: 'group_ids', label: t('Contact::Groups'), type: ResourceListFilterType.MULTI_RESOURCE, resourceType: 'contact_group' },
      { name: 'starred', label: t('Contact::Starred'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('Contacts::Yes'), value: String(true) }, { label: t('Contacts::No'), value: String(false) }] },
      { name: 'archived', label: t('Contact::Archived'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('Contacts::Yes'), value: String(true) }, { label: t('Contacts::No'), value: String(false) }] },
      { name: 'locale', label: t('Contact::Locale'), type: ResourceListFilterType.SINGLE_OPTION, options: LOCALES.map(locale => ({ label: t(`Locales::${locale}`), value: locale })) },
      { name: 'vat_number', label: t('Contact::VAT'), type: ResourceListFilterType.STRING },
      { name: 'hourly_rate', label: t('Contact::Hourly rate'), type: ResourceListFilterType.NUMBER },
      { name: 'day_rate', label: t('Contact::Day rate'), type: ResourceListFilterType.NUMBER },
      { name: 'marketing_consent', label: t('Contact::Marketing consent'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('Contacts::Yes'), value: String(true) }, { label: t('Contacts::No'), value: String(false) }] },
      { name: 'created_at', label: t('Contact::Created date'), type: ResourceListFilterType.DATE },
    ]

    const { headers, columns, selectedColumns, renderRow } = getContactTableRenderer({
      setting: setting,
      onRowClick: this.onTableContactClick,
      selectedItems: selectedContactIds,
      onSelectionChange: this.onTableRowSelectionChange,
      onActionClick: this.onTableActionClick,
    })

    return (
      <>
        <Helmet>
          <title>{t('Contacts::{{__appName}} | Contacts')}</title>
        </Helmet>
        <ScrollToTopOnMount />
        <NavigationDetection onRouteChange={this.onRouteChange} />
        <TopNavigation
          icon='user'
          title={t('Contacts::Contacts')}
          action={
            <TopNavigationButtonWithOptions
              icon='plus'
              text={t('Contacts::New contact')}
              actions={[
                { key: 'person', content: t('Contacts::Person'), icon: 'user' },
                { key: 'company', content: t('Contacts::Company'), icon: 'company' },
              ]}
              onActionClick={this.onTopNavigationActionClick}
            />
          }
        />

        <PageContent>
          <PageHeader
            title={t('Contacts::Contacts')}
            mainActions={[
              { key: 'export_contacts', content: t('Contacts::Export contacts'), icon: 'download-circle' },
            ]}
            onMainActionClick={this.onPageHeaderActionClick}
          />

          {!didInitialLoad && <PageLoader />}
          {didInitialLoad && <ResourceTable
            data={contacts}
            headers={headers}
            renderRow={renderRow}
            renderEmpty={<CardEmptyInfo
              icon={activeFiltersCount ? 'search' : 'user'}
              description={activeFiltersCount ? t('Contact::No contacts found') : t('Contact::No contacts have been created yet')}
              descriptionActionText={activeFiltersCount ? t('Contact::Clear filters') : t('Contact::Add new contact')}
              onDescriptionActionClick={activeFiltersCount ? this.onContactClearFilters : this.onNewContactClick}
            />}
            filters={filters}
            activeFilters={activeFilters}
            onFiltersChange={this.onContactFiltersChange}
            sortOptions={[
              { label: '-', value: '-' },
              { label: t('Contact::Name (A-Z)'), value: 'name_asc' },
              { label: t('Contact::Name (Z-A)'), value: 'name_desc' },
              { label: t('Contact::Alias (A-Z)'), value: 'alias_asc' },
              { label: t('Contact::Alias (Z-A)'), value: 'alias_desc' },
              { label: t('Contact::Created at ↑'), value: 'created_at_asc' },
              { label: t('Contact::Created at ↓'), value: 'created_at_desc' },
            ]}
            promotedBulkActions={promotedBulkActions}
            bulkActions={bulkActions}
            sortValue={sortValue}
            onSortChange={this.onContactSortValueChange}
            pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
            onPageChange={this.onPageChange}
            onPerPageChange={this.onPerPageChange}
            isLoading={isFetching}
            stickyHeader={true}
            selectedItems={selectedContactIds}
            onSelectionChange={this.onTableSelectionChange}
            searchValue={search}
            onSearchChange={this.onContactSearchChange}
            onSearchSubmit={this.onContactSearchSubmit}
            columns={columns}
            selectedColumns={selectedColumns}
            onColumnsChange={this.onColumnsChange}
            maxHeight='60vh'
          />}
        </PageContent>
      </>
    )
  }
}

interface IStateToProps {
  currentUser: CurrentUser
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    showContactModal: (options) => dispatch(showContactModal(options)),
    showConfirmModal: (options) => dispatch(showConfirmModal(options)),
    showBulkContactGroupModal: (options) => dispatch(showBulkContactGroupModal(options)),
    showExportContactsModal: (options) => dispatch(showExportContactsModal(options))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Contacts))